/**
 * Bra size finder
 * ---------------------------------------------------------------------------
 * Renders a Band + Cup selector that resolves to a recommended Size and keeps
 * that Size in sync with the product's variant picker / product form.
 *
 * Markup + config are produced by snippets/bra-size-finder.liquid
 */
(function () {
  if (window.customElements && window.customElements.get('bra-size-finder')) return;

  var SEP = '\u241F';

  function norm(value) {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  function eq(a, b) {
    return norm(a).toLowerCase() === norm(b).toLowerCase();
  }

  function mapKey(band, cup) {
    return norm(band).toLowerCase() + SEP + norm(cup).toLowerCase();
  }

  function attrEsc(value) {
    return norm(value).replace(/(["\\])/g, '\\$1');
  }

  function escapeHtml(value) {
    return norm(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function fire(el) {
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  class BraSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.bsfInit) return;

      var configEl = this.querySelector('[data-bsf-config]');
      if (!configEl) return;

      try {
        this.cfg = JSON.parse(configEl.textContent);
      } catch (error) {
        return;
      }

      this.bsfInit = true;
      this.isPushing = false;

      this.bandSelect = this.querySelector('[data-bsf-band]');
      this.cupSelect = this.querySelector('[data-bsf-cup]');
      this.sizeSelect = this.querySelector('[data-bsf-size]');
      this.valueEl = this.querySelector('[data-bsf-selected]');
      this.resultEl = this.querySelector('[data-bsf-result]');
      this.resultTextEl = this.querySelector('[data-bsf-result-text]');
      this.noteEl = this.querySelector('[data-bsf-note]');

      this.bands = (this.cfg.bands || []).map(norm).filter(Boolean);
      this.cups = (this.cfg.cups || []).map(norm).filter(Boolean);
      this.map = this.parseMatrix(this.cfg.matrix);

      this.bindEvents();
      this.hydrate();
    }

    disconnectedCallback() {
      if (this.onPickerChange) {
        document.removeEventListener('change', this.onPickerChange);
      }
    }

    /* ------------------------------------------------------------ data --- */

    /**
     * Accepted row formats (one row per cup, columns follow the band order):
     *   "A: S,S,S,M,M,L,XL,XL,2XL,2XL"
     *   "S,S,S,M,M,L,XL,XL,2XL,2XL"      (cup taken from its row index)
     * Empty cells ("", "-", "x") are treated as "no recommendation".
     */
    parseMatrix(raw) {
      var map = {};
      if (!raw) return map;

      var rows = String(raw).split(/\r?\n/);
      var rowIndex = 0;

      for (var i = 0; i < rows.length; i++) {
        var line = norm(rows[i]);
        if (!line) continue;

        var cup;
        var valuesRaw;
        var colon = line.indexOf(':');

        if (colon > -1) {
          cup = norm(line.slice(0, colon));
          valuesRaw = line.slice(colon + 1);
        } else {
          cup = this.cups[rowIndex] || '';
          valuesRaw = line;
        }
        rowIndex++;
        if (!cup) continue;

        var values = valuesRaw.split(',');
        for (var c = 0; c < values.length; c++) {
          var band = this.bands[c];
          var size = norm(values[c]);
          if (!band || !size || size === '-' || size === '—' || size.toLowerCase() === 'x') continue;
          map[mapKey(band, cup)] = size;
        }
      }

      return map;
    }

    sizeFor(band, cup) {
      if (!band || !cup) return '';
      return this.map[mapKey(band, cup)] || '';
    }

    combosForSize(size) {
      var out = [];
      if (!size) return out;

      var format = this.cfg.comboFormat || '[band][cup]';
      for (var b = 0; b < this.bands.length; b++) {
        for (var c = 0; c < this.cups.length; c++) {
          if (eq(this.map[mapKey(this.bands[b], this.cups[c])], size)) {
            out.push(format.replace('[band]', this.bands[b]).replace('[cup]', this.cups[c]));
          }
        }
      }
      return out;
    }

    sizeOption(size) {
      if (!this.sizeSelect || !size) return null;
      var options = this.sizeSelect.options;
      for (var i = 0; i < options.length; i++) {
        if (options[i].value && eq(options[i].value, size)) return options[i];
      }
      return null;
    }

    /* ---------------------------------------------------------- events --- */

    bindEvents() {
      var self = this;

      if (this.bandSelect) {
        this.bandSelect.addEventListener('change', function () {
          self.resolveFromBandCup();
        });
      }

      if (this.cupSelect) {
        this.cupSelect.addEventListener('change', function () {
          self.resolveFromBandCup();
        });
      }

      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', function () {
          if (self.cfg.clearOnSizeSelect) self.clearBandCup();
          self.apply(self.sizeSelect.value, { push: true });
        });
      }

      if (!this.cfg.sync) return;

      // Delegated so it survives a section re-render replacing the variant picker.
      this.onPickerChange = function (event) {
        if (self.isPushing) return;

        var target = event.target;
        if (!target || !target.name) return;

        var name = self.cfg.optionName;
        var isSelect = target.tagName === 'SELECT' && target.name === 'options[' + name + ']';
        var isRadio = target.type === 'radio' && target.name === name;
        if (!isSelect && !isRadio) return;
        if (!target.closest || !target.closest('variant-selects')) return;

        var picker = self.getPicker();
        if (picker && !picker.contains(target)) return;

        var value = self.readPickerSize();
        if (!value || eq(value, self.currentSize)) return;

        self.clearBandCup();
        self.apply(value, { push: false });
      };
      document.addEventListener('change', this.onPickerChange);
    }

    /* ------------------------------------------------------ resolution --- */

    resolveFromBandCup() {
      var band = this.bandSelect ? this.bandSelect.value : '';
      var cup = this.cupSelect ? this.cupSelect.value : '';

      if (!band || !cup) {
        this.apply('', { push: false });
        return;
      }

      var size = this.sizeFor(band, cup);

      if (!size) {
        this.apply('', { push: false });
        this.showNote(this.cfg.noMatchText);
        return;
      }

      if (!this.sizeOption(size)) {
        this.apply('', { push: false });
        this.showNote((this.cfg.unavailableText || '').replace(/\[size\]/g, escapeHtml(size)));
        return;
      }

      this.apply(size, { push: true });
    }

    clearBandCup() {
      if (this.bandSelect) this.bandSelect.value = '';
      if (this.cupSelect) this.cupSelect.value = '';
    }

    /**
     * Single entry point that updates every visible piece of the UI.
     */
    apply(size, options) {
      options = options || {};
      var option = this.sizeOption(size);
      var value = option ? option.value : '';

      this.currentSize = value;

      if (this.sizeSelect) this.sizeSelect.value = value;

      if (this.valueEl) {
        this.valueEl.textContent = value || this.cfg.placeholder || '—';
        this.valueEl.classList.toggle('bra-size-finder__value--empty', !value);
      }

      this.classList.toggle('bra-size-finder--resolved', !!value);
      this.hideNote();
      this.renderResult(value);

      if (value && option && option.dataset.available === 'false' && this.cfg.soldOutText) {
        this.showNote(this.cfg.soldOutText.replace(/\[size\]/g, escapeHtml(value)));
      }

      if (options.push && value && this.cfg.sync) this.push(value);

      // Anything else on the page that mirrors the size (the bundle rows of the
      // Buy buttons block) listens for this, because pushing a size the variant
      // picker already holds fires no change event of its own.
      this.dispatchEvent(
        new CustomEvent('bsf:size', { bubbles: true, detail: { size: value, uid: this.cfg.uid } })
      );
    }

    renderResult(size) {
      if (!this.resultEl || !this.resultTextEl) return;

      if (!size || !this.cfg.showResult) {
        this.resultEl.hidden = true;
        return;
      }

      var combos = this.combosForSize(size);
      if (!combos.length) {
        this.resultEl.hidden = true;
        return;
      }

      var total = combos.length;
      var suffix = '';

      if (this.cfg.resultMax > 0 && combos.length > this.cfg.resultMax) {
        suffix = (this.cfg.resultMoreText || '').replace(/\[count\]/g, String(combos.length - this.cfg.resultMax));
        combos = combos.slice(0, this.cfg.resultMax);
      }

      var list = combos.map(escapeHtml).join(this.cfg.comboSeparator || ', ') + suffix;

      this.resultTextEl.innerHTML = (this.cfg.resultTemplate || '')
        .replace(/\[size\]/g, escapeHtml(size))
        .replace(/\[combinations\]/g, list)
        .replace(/\[count\]/g, String(total));

      this.resultEl.hidden = false;
    }

    showNote(html) {
      if (!this.noteEl || !html) return;
      this.noteEl.innerHTML = html;
      this.noteEl.hidden = false;
    }

    hideNote() {
      if (!this.noteEl) return;
      this.noteEl.hidden = true;
      this.noteEl.innerHTML = '';
    }

    /* ------------------------------------------------- product syncing --- */

    // Never cached: the theme can re-render the product section and replace it.
    getPicker() {
      var el = null;
      if (this.cfg.sectionId) el = document.getElementById('variant-selects-' + this.cfg.sectionId);
      if (!el) {
        var info = this.closest('product-info');
        if (info) el = info.querySelector('variant-selects');
      }
      if (!el) el = document.querySelector('variant-selects');
      return el;
    }

    pickerField() {
      var picker = this.getPicker();
      if (!picker) return null;

      var name = this.cfg.optionName;
      var select = picker.querySelector('select[name="options[' + attrEsc(name) + ']"]');
      if (select) return { type: 'select', el: select };

      var radios = picker.querySelectorAll('input[type="radio"][name="' + attrEsc(name) + '"]');
      if (radios.length) return { type: 'radio', el: radios };

      return null;
    }

    readPickerSize() {
      var field = this.pickerField();
      if (!field) return null;

      if (field.type === 'select') return field.el.value;

      for (var i = 0; i < field.el.length; i++) {
        if (field.el[i].checked) return field.el[i].value;
      }
      return null;
    }

    push(size) {
      // Guards the delegated listener below: every dispatch here is synchronous,
      // including any option the theme auto-corrects while handling it.
      this.isPushing = true;

      try {
        var field = this.pickerField();
        var pushed = false;

        if (field && field.type === 'select') {
          for (var i = 0; i < field.el.options.length; i++) {
            if (eq(field.el.options[i].value, size)) {
              field.el.selectedIndex = i;
              fire(field.el);
              pushed = true;
              break;
            }
          }
        } else if (field) {
          for (var r = 0; r < field.el.length; r++) {
            if (eq(field.el[r].value, size)) {
              field.el[r].checked = true;
              fire(field.el[r]);
              pushed = true;
              break;
            }
          }
        }

        if (!pushed) this.pushDirect(size);
      } finally {
        this.isPushing = false;
      }
    }

    variantIdInputs() {
      var list = [];
      var seen = [];

      var add = function (nodes) {
        for (var i = 0; i < nodes.length; i++) {
          if (seen.indexOf(nodes[i]) === -1) {
            seen.push(nodes[i]);
            list.push(nodes[i]);
          }
        }
      };

      if (this.cfg.formId) {
        add(document.querySelectorAll('form[id="' + attrEsc(this.cfg.formId) + '"] input[name="id"]'));
        add(
          document.querySelectorAll(
            'form[id="' + attrEsc(this.cfg.formId.replace(/^product-form-/, 'product-form-installment-')) + '"] input[name="id"]'
          )
        );
      }

      if (!list.length) {
        var scope = this.closest('product-info') || document;
        add(scope.querySelectorAll('form[action*="/cart/add"] input[name="id"]'));
      }

      return list;
    }

    pushDirect(size) {
      var variants = this.cfg.variants || [];
      var inputs = this.variantIdInputs();
      if (!variants.length || !inputs.length) return false;

      var index = (this.cfg.optionPosition || 1) - 1;
      var currentId = parseInt(inputs[0].value, 10);
      var current = null;

      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === currentId) {
          current = variants[i];
          break;
        }
      }

      var sameOthers = function (variant) {
        if (!current) return true;
        for (var o = 0; o < variant.o.length; o++) {
          if (o !== index && !eq(variant.o[o], current.o[o])) return false;
        }
        return true;
      };

      var candidates = variants.filter(function (variant) {
        return eq(variant.o[index], size);
      });

      var match =
        candidates.filter(function (v) {
          return v.a && sameOthers(v);
        })[0] ||
        candidates.filter(sameOthers)[0] ||
        candidates.filter(function (v) {
          return v.a;
        })[0] ||
        candidates[0];

      if (!match) return false;

      inputs.forEach(function (input) {
        input.value = match.id;
        fire(input);
      });

      return true;
    }

    /* ------------------------------------------------------- first run --- */

    hydrate() {
      if (this.cfg.initialState === 'variant') {
        var current = this.readPickerSize() || (this.sizeSelect ? this.sizeSelect.dataset.initial : '');
        if (current) {
          this.apply(current, { push: false });
          return;
        }
      }
      this.apply('', { push: false });
    }
  }

  customElements.define('bra-size-finder', BraSizeFinder);
})();
